'use client';

import { useEffect, useState } from 'react';

import { SessionNotFound } from '@northladder/design-system';
import { IApiError } from '@northladder/utilities';

import { AssessmentCompleteSuccess } from '../assessment/complete-screen';
import { useNavRouter } from '../hooks';

import { ErrorInValidatingSession } from './ErrorInValidatingSession';
import { InvalidSession } from './InvalidSession';
import { ValidatingSessionLoader } from './ValidatingSessionLoader';

interface INoImagesLoadedYetProps {
  error: IApiError | null;
  hasInvalidEvaluationId: boolean;
  hasInvalidSession: boolean;
  hasNoEvaluationId: boolean;
  isAssessmentDone: boolean;
  isLoading: boolean;
  onRestart: () => void;
  onRetry: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * This is rendered for all cases when there is no valid evaluation ID in session
 * such as when still validating from the backend, when expired, or when not
 * present in the URL at all.
 */
export function NoValidAssessmentSession({
  error,
  hasInvalidEvaluationId,
  hasInvalidSession,
  hasNoEvaluationId,
  isAssessmentDone,
  isLoading,
  onRestart,
  onRetry,
}: INoImagesLoadedYetProps) {
  /**
   * Adding the fallback state to show the loading state for the fallback.
   * Will show a simple spinner in the center of the screen for 2 seconds before
   * rendering the actual fallback state. That way simple API errors don't get
   * logged accidentally.
   */
  const [isPreparingFallback, setIsPreparingFallback] = useState(true);

  const { evaluationId } = useNavRouter();

  useEffect(() => {
    // Simulate loading by setting a timeout
    const timeoutId = setTimeout(() => {
      setIsPreparingFallback(false);
    }, 2000);

    // Clear the timeout to prevent memory leaks
    return () => {
      clearTimeout(timeoutId);
      setIsPreparingFallback(false);
    };
  }, [isLoading]);

  if (isPreparingFallback) {
    return <ValidatingSessionLoader />;
  }

  if (hasInvalidSession) {
    return <InvalidSession />;
  }

  if (hasInvalidEvaluationId || hasNoEvaluationId) {
    return <SessionNotFound />;
  }

  if (isLoading) {
    return <ValidatingSessionLoader />;
  }

  if (error) {
    return <ErrorInValidatingSession onRestart={onRestart} onRetry={onRetry} />;
  }

  if (isAssessmentDone) {
    return <AssessmentCompleteSuccess evaluationId={evaluationId} />;
  }

  return null;
}
